'use client';

import React, { useState } from 'react';
import {
  Search,
  Filter,
  ChevronDown,
  ChevronUp,
  ArrowUpDown,
  Layers,
} from 'lucide-react';
import { useQuery } from '@apollo/client/react';
import {
  COMMISSIONS_QUERY,
  LOCATION_CODES_QUERY,
  DOCUMENT_STATS_QUERY,
  type DocumentType,
  DocumentType as DocumentTypeEnum,
  type DocumentStats,
} from '@paeffgen/react/crm-documents/data-access-graphql';
import { useAdmin } from '@paeffgen/react/crm-documents/feature-auth';
import {
  TYPE_BADGES,
  useDebouncedInput,
  useSearchField,
} from '@paeffgen/react/crm-documents/util';
import { SearchClearButton } from '@paeffgen/react/crm-documents/ui';
import { clsx } from 'clsx';
import { BelegeFilterSheet } from './belege-filter-sheet';
import { BelegeSortSheet, type SortConfig } from './belege-sort-sheet';

export interface FilterState {
  search: string;
  type: DocumentType[];
  dateRange: 'ALL' | '30DAYS' | '3MONTHS' | '6MONTHS' | '1YEAR' | 'CUSTOM';
  startDate?: string;
  endDate?: string;
  minAmount?: string;
  maxAmount?: string;
  location?: string;
  commission?: string;
  yourReference?: string;
}

/** Leerer Filterzustand — geteilt mit der „Zurücksetzen"-Aktion in der Beleg-Liste. */
export const EMPTY_FILTER_STATE: FilterState = {
  search: '',
  type: [],
  dateRange: 'ALL',
  startDate: undefined,
  endDate: undefined,
  minAmount: '',
  maxAmount: '',
  location: '',
  commission: '',
  yourReference: '',
};

/** Lesbare Labels für den Zeitraum — geteilt zwischen dem Select und den
 *  Filter-Chips in der Beleg-Liste (single source of truth). */
export const DATE_RANGE_LABELS: Record<FilterState['dateRange'], string> = {
  ALL: 'Gesamter Zeitraum',
  '30DAYS': 'Letzte 30 Tage',
  '3MONTHS': 'Letzte 3 Monate',
  '6MONTHS': 'Letzte 6 Monate',
  '1YEAR': 'Letztes Jahr',
  CUSTOM: 'Benutzerdefiniert',
};

/** Anzahl aktiver Filter — geteilt mit der „Filter aktiv"-Zeile in der Beleg-Liste. */
export function countActiveFilters(filters: FilterState): number {
  return (
    (filters.search ? 1 : 0) +
    (filters.type.length > 0 ? 1 : 0) +
    (filters.dateRange !== 'ALL' ? 1 : 0) +
    (filters.minAmount || filters.maxAmount ? 1 : 0) +
    (filters.location ? 1 : 0) +
    (filters.commission ? 1 : 0) +
    (filters.yourReference ? 1 : 0)
  );
}

interface DocumentFiltersProps {
  filters: FilterState;
  onFilterChange: (filters: FilterState) => void;
  sortConfig: SortConfig;
  onSortChange: (sort: SortConfig) => void;
}

export function DocumentFilters({
  filters,
  onFilterChange,
  sortConfig,
  onSortChange,
}: DocumentFiltersProps) {
  const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  const [mobileSortOpen, setMobileSortOpen] = useState(false);
  const { adminQueryVars, isAdmin, allCustomers } = useAdmin();
  const bannerVisible = isAdmin && allCustomers;

  const { data: statsData } = useQuery<{ documentStats: DocumentStats }>(
    DOCUMENT_STATS_QUERY,
    { variables: { ...adminQueryVars }, fetchPolicy: 'cache-and-network' }
  );

  const { data: commissionsData } = useQuery<{ commissions: string[] }>(
    COMMISSIONS_QUERY,
    { variables: { ...adminQueryVars }, fetchPolicy: 'cache-first' }
  );

  const { data: locationData } = useQuery<{ locationCodes: string[] }>(
    LOCATION_CODES_QUERY,
    { variables: { ...adminQueryVars }, fetchPolicy: 'cache-first' }
  );

  const commissions = commissionsData?.commissions ?? [];
  const locationCodes = locationData?.locationCodes ?? [];

  const counts: Record<string, number> = { total: 0 };
  if (statsData?.documentStats) {
    counts.total = statsData.documentStats.totalDocuments;
    for (const entry of statsData.documentStats.byType) {
      counts[entry.type] = entry.count;
    }
  }

  // Lokaler Input-State, der die Tastatureingaben sofort spiegelt.
  // Der URL-/Apollo-Sync via onFilterChange erfolgt erst nach 300 ms Pause,
  // damit schnelles Tippen nicht durch router.replace()-Re-Renders blockiert
  // wird (sonst gehen Buchstaben verloren).
  const [searchInput, setSearchInput, commitSearch] = useDebouncedInput(
    filters.search,
    (value) => onFilterChange({ ...filters, search: value }),
    300,
  );

  // ✕ und Escape umgehen den Debounce; die Props werden auf BEIDE Inputs
  // gespreizt (Mobile-Leiste und Desktop-Karte teilen sich diesen Zustand).
  const search = useSearchField({
    value: searchInput,
    onChange: setSearchInput,
    onCommit: commitSearch,
  });

  const handleTypeChange = (type: DocumentType | 'ALL') => {
    let newTypes: DocumentType[] = [];

    if (type === 'ALL') {
      newTypes = [];
    } else {
      // Single-Select (Radio): bisherige Auswahl ersetzen.
      // Erneuter Klick auf den aktiven Typ setzt denselben State → bleibt aktiv.
      newTypes = [type];
    }

    onFilterChange({ ...filters, type: newTypes });
  };

  const handleDateRangeChange = (range: FilterState['dateRange']) => {
    const newFilters = { ...filters, dateRange: range };
    if (range !== 'CUSTOM') {
      delete newFilters.startDate;
      delete newFilters.endDate;
    }
    onFilterChange(newFilters);
  };

  const activeFilterCount = countActiveFilters(filters);
  const hasActiveFilters = activeFilterCount > 0;

  return (
    <>
      {/* Mobile-only compact toolbar: Search + Filter button + Sort button.
          WICHTIG: Liegt als Geschwister NEBEN dem space-y-4-Block, damit der
          Containing Block für position:sticky der DocumentList-Wurzel
          (space-y-6) entspricht — sonst klebt der Sticky-Block nur kurz und
          scrollt mit dem viel kürzeren inneren Filter-Wrapper raus.
          Top-Offset richtet sich nach Mobile-Header (h-14) + optionalem
          Admin-Banner (+2.5rem). */}
      <div
        className={clsx(
          'md:hidden sticky z-30 bg-white border-y border-gray-200 px-4 py-2 -mx-4',
          bannerVisible ? 'top-[6rem]' : 'top-14',
        )}
      >
        <div className="flex items-center gap-2">
          <div className="flex-1 relative">
            <Search
              size={18}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none"
            />
            <input
              type="text"
              inputMode="search"
              {...search.inputProps}
              placeholder="Suchen…"
              className="w-full pl-9 pr-10 h-10 rounded-lg border border-zinc-200 bg-white text-sm placeholder:text-zinc-400 focus:border-primary focus:ring-1 focus:ring-primary/20 outline-none"
            />
            {search.hasValue && (
              <SearchClearButton
                onClick={search.clear}
                size={14}
                className="absolute right-1.5 top-1/2 -translate-y-1/2"
              />
            )}
          </div>
          <button
            type="button"
            onClick={() => setMobileFilterOpen(true)}
            aria-label="Filter öffnen"
            className={clsx(
              'w-10 h-10 rounded-lg border flex items-center justify-center transition-colors shrink-0',
              hasActiveFilters
                ? 'border-primary bg-orange-50 text-primary'
                : 'border-zinc-200 bg-white text-zinc-600 hover:bg-zinc-50',
            )}
          >
            <Filter size={18} />
          </button>
          <button
            type="button"
            onClick={() => setMobileSortOpen(true)}
            aria-label="Sortieren"
            className="relative w-10 h-10 rounded-lg border border-zinc-200 bg-white text-zinc-600 hover:bg-zinc-50 flex items-center justify-center transition-colors shrink-0"
          >
            <ArrowUpDown size={18} />
            <span className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full bg-primary text-white text-[9px] font-bold flex items-center justify-center border-2 border-white leading-none">
              {sortConfig.direction === 'desc' ? '↓' : '↑'}
            </span>
          </button>
        </div>
      </div>

      {/* Non-sticky Filter-Bereich (Desktop-Card, Advanced, Type-Cards, Status-Pill).
          md:!mt-0 kompensiert den von DocumentList's space-y-6 erzeugten margin-top,
          damit auf Desktop kein zusätzlicher Abstand entsteht (die mobile Sticky-Bar
          wird via md:hidden ausgeblendet, zählt aber im CSS-Sibling-Selector mit). */}
      <div className="space-y-4 mb-6 md:!mt-0">
      {/* Desktop Search and Main Filters */}
      <div className="hidden md:flex bg-white p-4 rounded-xl shadow-sm border border-gray-200 flex-col md:flex-row gap-4 items-stretch md:items-center">
        <div className="relative w-full md:flex-1">
          <Search
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            size={20}
          />
          <input
            type="text"
            {...search.inputProps}
            placeholder="Anfrage, Angebot, Baustelle, Artikel suchen..."
            className="w-full pl-10 pr-10 py-2 md:py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-shadow text-sm"
          />
          {search.hasValue && (
            <SearchClearButton
              onClick={search.clear}
              className="absolute right-2 top-1/2 -translate-y-1/2"
            />
          )}
        </div>

        <div className="flex flex-col md:flex-row gap-3 w-full md:w-auto items-stretch md:items-center">
          <select
            className="px-4 py-2 text-sm border border-gray-300 rounded-lg bg-white outline-none focus:border-primary"
            value={filters.dateRange}
            onChange={(e) =>
              handleDateRangeChange(
                e.target.value as FilterState['dateRange']
              )
            }
          >
            {(
              Object.entries(DATE_RANGE_LABELS) as [
                FilterState['dateRange'],
                string,
              ][]
            ).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>

          {filters.dateRange === 'CUSTOM' && (
            <div className="flex items-center gap-2 animate-in slide-in-from-left-2 duration-200">
              <input
                type="date"
                value={filters.startDate || ''}
                onChange={(e) =>
                  onFilterChange({ ...filters, startDate: e.target.value })
                }
                className="px-3 py-2 border border-gray-300 rounded-lg outline-none focus:border-primary text-sm flex-1"
                placeholder="Von"
              />
              <span className="text-gray-400">-</span>
              <input
                type="date"
                value={filters.endDate || ''}
                onChange={(e) =>
                  onFilterChange({ ...filters, endDate: e.target.value })
                }
                className="px-3 py-2 border border-gray-300 rounded-lg outline-none focus:border-primary text-sm flex-1"
                placeholder="Bis"
              />
            </div>
          )}

          <button
            onClick={() => setIsAdvancedOpen(!isAdvancedOpen)}
            className={clsx(
              'px-4 py-2 text-sm border rounded-lg flex items-center justify-center gap-2 hover:bg-gray-50 whitespace-nowrap transition-colors',
              isAdvancedOpen ||
                filters.minAmount ||
                filters.maxAmount ||
                filters.location ||
                filters.commission ||
                filters.yourReference
                ? 'border-primary text-primary bg-orange-50'
                : 'border-gray-300 bg-white text-gray-700'
            )}
          >
            <Filter size={18} />
            <span>Erweiterte Filter</span>
            {isAdvancedOpen ? (
              <ChevronUp size={16} />
            ) : (
              <ChevronDown size={16} />
            )}
          </button>
        </div>
      </div>

      {/* Advanced Filters Panel */}
      {isAdvancedOpen && (
        <div className="p-4 bg-gray-50 rounded-xl border border-gray-200 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 animate-in slide-in-from-top-2 duration-200">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">
              Betrag (Netto)
            </label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                placeholder="Von"
                value={filters.minAmount}
                onChange={(e) =>
                  onFilterChange({ ...filters, minAmount: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:border-primary"
              />
              <span className="text-gray-400">-</span>
              <input
                type="number"
                placeholder="Bis"
                value={filters.maxAmount}
                onChange={(e) =>
                  onFilterChange({ ...filters, maxAmount: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:border-primary"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">
              Standort
            </label>
            <select
              value={filters.location}
              onChange={(e) =>
                onFilterChange({ ...filters, location: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:border-primary bg-white"
            >
              <option value="">Alle Standorte</option>
              {locationCodes.map((code) => (
                <option key={code} value={code}>
                  {code}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">
              Kommission / Baustelle
            </label>
            <select
              value={filters.commission || ''}
              onChange={(e) =>
                onFilterChange({ ...filters, commission: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:border-primary bg-white"
            >
              <option value="">Alle Kommissionen</option>
              {commissions.map((comm) => (
                <option key={comm} value={comm}>
                  {comm}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">
              Ihre Referenz
            </label>
            <input
              type="text"
              placeholder="Referenz suchen..."
              value={filters.yourReference || ''}
              onChange={(e) =>
                onFilterChange({ ...filters, yourReference: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:border-primary"
            />
          </div>
        </div>
      )}

      {/* Type Filter Cards */}
      <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
        {(['ALL', ...Object.values(DocumentTypeEnum)] as const).map((type) => {
          const isAll = type === 'ALL';
          const isSelected = isAll
            ? filters.type.length === 0
            : filters.type.includes(type as DocumentType);
          const label = isAll
            ? 'Alle'
            : type.charAt(0) + type.slice(1).toLowerCase();
          const count = isAll ? counts.total : counts[type] || 0;

          return (
            <button
              key={type}
              type="button"
              onClick={() => handleTypeChange(type)}
              aria-pressed={isSelected}
              className={clsx(
                'bg-white rounded-xl p-3 text-left transition-colors hover:bg-zinc-50',
                isSelected
                  ? 'border-2 border-zinc-900'
                  : 'border border-zinc-200',
              )}
            >
              <div className="flex items-start justify-between gap-2 mb-2">
                <span
                  className={clsx(
                    'w-8 h-8 rounded-lg border flex items-center justify-center text-xs font-bold shrink-0',
                    isAll
                      ? 'bg-zinc-100 text-zinc-700 border-zinc-200'
                      : TYPE_BADGES[type as DocumentType],
                  )}
                >
                  {isAll ? <Layers size={16} /> : type.charAt(0)}
                </span>
                <span
                  className={clsx(
                    'text-lg font-bold tabular-nums',
                    count === 0 ? 'text-zinc-400' : 'text-zinc-900',
                  )}
                >
                  {count}
                </span>
              </div>
              <p className="text-xs font-medium text-zinc-600 truncate">
                {label}
              </p>
            </button>
          );
        })}
      </div>

      {/* „Filter aktiv"-Zeile lebt jetzt in der Controls-Zeile der
          DocumentList (links neben der Rechnungssummen-Card). */}
      </div>

      <BelegeFilterSheet
        open={mobileFilterOpen}
        onClose={() => setMobileFilterOpen(false)}
        filters={filters}
        onFilterChange={onFilterChange}
      />
      <BelegeSortSheet
        open={mobileSortOpen}
        onClose={() => setMobileSortOpen(false)}
        sortConfig={sortConfig}
        onSortChange={onSortChange}
      />
    </>
  );
}
